import { Select, Typography } from 'antd'
import { LabeledValue } from 'antd/es/select'
import { DrilldownTypeAndFieldSelect } from 'features/drilldown/components/fieldSelect/TypeAndFieldSelect'
import { HistogramConfigurationOption } from 'features/drilldown/components/histogram/configPanel/histogramConfigTypes'
import { useHistogramConfiguration } from 'features/drilldown/components/histogram/contexts/HistogramConfigurationContext'
import {
  HISTOGRAM_DATE_BUCKETING_OPTIONS,
  HistogramDateBucketingOption,
} from 'features/drilldown/components/histogram/histogramDateConfigurationOptions'
import { useDrilldownSchema } from 'features/drilldown/rolodex/DrilldownDataSchema'
import { DrilldownField } from 'features/drilldown/rolodex/ontology/ontologyTypes'
import { FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { HistoAggregationOpSelect } from './HistoAggregationOpSelect'

const { Text } = Typography

const ConfigContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`

const ConfigGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ConfigLabel = styled(Text)`
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
`

export interface HistogramGroupByConfigurationOption
  extends HistogramConfigurationOption {
  // Generally for rolodex histogram groupByQueries:
  // We use 'field' to filter on top level properties like 'id' and
  // 'key' to filter on properties under 'data' like 'date'
  histogramGroupByPropertyKey: 'key' | 'field'

  // Do we truncate the results of the histogram.
  // For certain histogram types such as dates, truncation doesn't make sense
  shouldTruncateResults: boolean

  // Whether a user can switch a histogram to group on this value.
  isSelectableInUi?: boolean
}

export const HistoAggregationConfigView: FC = () => {
  const {
    groupingColumn,
    setGroupingColumn,
    aggregationColumn,
    setAggregationColumn,
    dateBucketingConfiguration,
    setDateBucketingConfiguration,
    setAggregationOp,
  } = useHistogramConfiguration()
  const { fields, isDescendant } = useDrilldownSchema()
  const groupingColumnDef = fields[groupingColumn]

  const isAggregationOptionValid = useCallback(
    (aggCol: DrilldownField) => {
      if (!aggCol) {
        return false
      }
      const { entryType } = fields[aggCol]
      return (
        groupingColumnDef.entryType === entryType ||
        isDescendant(entryType, groupingColumnDef.entryType)
      )
    },
    [fields, groupingColumnDef.entryType, isDescendant]
  )

  // Only allow aggregations of rolodex types at levels downstream of the one currently selected
  const allowedAggregationFields = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fields).filter(([field]) =>
          isAggregationOptionValid(field)
        )
      ),
    [fields, isAggregationOptionValid]
  )

  const onDateGroupingOptionChange = useCallback(
    (value: LabeledValue) => {
      setDateBucketingConfiguration?.(
        HISTOGRAM_DATE_BUCKETING_OPTIONS[
          value.value as HistogramDateBucketingOption
        ]
      )
    },
    [setDateBucketingConfiguration]
  )

  const dateBucketingConfigurationOptions = useMemo(
    () => Object.values(HISTOGRAM_DATE_BUCKETING_OPTIONS),
    []
  )

  const areColumnsConfigurable =
    !!setGroupingColumn ||
    !!setAggregationColumn ||
    !!setDateBucketingConfiguration

  if (!areColumnsConfigurable) {
    return null
  }

  return (
    <ConfigContainer>
      {!!setGroupingColumn && (
        <ConfigGroup>
          <ConfigLabel>Group by: </ConfigLabel>
          <DrilldownTypeAndFieldSelect
            selectedField={groupingColumn}
            setSelectedField={setGroupingColumn}
          />
        </ConfigGroup>
      )}

      {!!setAggregationColumn && (
        <ConfigGroup>
          <ConfigLabel>Aggregate:</ConfigLabel>
          <DrilldownTypeAndFieldSelect
            selectedField={aggregationColumn}
            setSelectedField={setAggregationColumn}
            fields={allowedAggregationFields}
          />
        </ConfigGroup>
      )}

      {!!setAggregationOp && (
        <ConfigGroup>
          <ConfigLabel>Method: </ConfigLabel>
          <HistoAggregationOpSelect />
        </ConfigGroup>
      )}

      {!!setDateBucketingConfiguration && (
        <ConfigGroup>
          <ConfigLabel>Time period: </ConfigLabel>
          <Select
            style={{ minWidth: 130 }}
            labelInValue={true}
            options={dateBucketingConfigurationOptions}
            value={dateBucketingConfiguration}
            onChange={onDateGroupingOptionChange}
            placeholder='Select period'
          />
        </ConfigGroup>
      )}
    </ConfigContainer>
  )
}
